import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useGetCompanies } from '../../../api/masters/masters';
import { ErrorInfo, UserSearchConditions } from '../../../api/model';
import { usePostUsersSearch } from '../../../api/users/users';
import { useDefaultOptions } from '../../../hooks/api';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import { UserList } from './UserList';
import { UserSearch } from './UserSearch';
import { useGetPrivileges } from '../../../api/privilege/privilege';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|ユーザー一覧')}</title>
    </Helmet>
  );
}

function UserListPage() {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 検索条件部分が開いているかどうか
  const [openSearchConditions, setOpenSearchConditions] = useState(false);
  const companies = useGetCompanies(options);
  const privileges = useGetPrivileges(options);

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<UserSearchConditions>(
    sessionStorageUtils.get<UserSearchConditions>('Users.SearchConditions', {})
  );

  // 検索条件
  const refConditions = useRef(sessionStorageUtils.get<UserSearchConditions>('Users.SearchConditions', {}));

  // 検索 API 情報
  const {
    data,
    isLoading,
    isError,
    error,
    mutateAsync: searchAsync,
  } = usePostUsersSearch<AxiosError<ErrorInfo>, unknown>(options);

  // 一覧の初回表示
  useEffect(() => {
    const postUsers = async () => {
      await searchAsync({ data: defaultConditions });
    };
    void postUsers();
  }, [defaultConditions, searchAsync]);

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: UserSearchConditions) => {
      const postUsers = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          sessionStorageUtils.set('Users.SearchConditions', next);
          refConditions.current = next;
        };

        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      void postUsers();
    },
    [searchAsync]
  );

  // 条件を保存し、検索する
  const postUsersDelete = async () => {
    // API アクセスの実行
    await searchAsync({ data: refConditions.current });
  };

  return (
    <div id="main">
      <Title />
      <section>
        <h3 className="trig">
          <span>
            {t('ユーザー検索')}
            {/* 検索条件の開閉 */}
            <button
              type="button"
              className="text-button"
              onClick={(e) => {
                e.preventDefault();
                setOpenSearchConditions((pre) => !pre);
              }}
            >
              <i className={`fa-solid ${openSearchConditions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>

        <UserSearch
          openSearchConditions={openSearchConditions}
          license={data?.data.license ?? ''}
          defaultConditions={defaultConditions}
          companies={companies.data?.data.companies ?? []}
          privileges={privileges.data?.data.privileges_list_items ?? []}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
        />
        <UserList data={data} isLoading={isLoading} isError={isError} error={error} refetch={postUsersDelete} />
      </section>
    </div>
  );
}

export default UserListPage;
